import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

/**
 * Bridges Auth ↔ Language so a signed-in user's preferred_lang persists across
 * devices and visits. Runs invisibly inside the app tree.
 *
 * Rules:
 *  - When user first loads and has a preferred_lang, mirror it to the UI.
 *  - When the UI language changes while signed in, PATCH it to the backend.
 *  - Guards prevent an initial sync from firing a PATCH loop.
 */
export default function LanguagePersistBridge() {
  const { user } = useAuth();
  const { lang, setLang } = useLang();
  const hydratedForUserId = useRef(null);
  const suppressNextPatch = useRef(false);

  // Pull preferred_lang once per signed-in user
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (hydratedForUserId.current === user.id) return;
    hydratedForUserId.current = user.id;
    const pref = user.preferred_lang;
    if ((pref === "en" || pref === "es") && pref !== lang) {
      suppressNextPatch.current = true;
      setLang(pref);
    }
  }, [user, lang, setLang]);

  // Push language changes back to the server
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (suppressNextPatch.current) {
      suppressNextPatch.current = false;
      return;
    }
    if (user.preferred_lang === lang) return;
    api.patch("/auth/me/language", { lang }).catch(() => {});
  }, [lang, user]);

  return null;
}
